import { Button } from '@/components/ui/Button';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import BASIC_CONSTANT from '../../constants/basic.constants.js/basic.constants.js';
import URL_CONSTANT from '../../constants/url.constants.js';

function MenuItem({ href, onClick, children, id, target, rel }) {
  const baseClass =
    'block w-full py-2 text-[13px] text-center text-white/90 transition-all duration-200 hover:text-[#ddfc10]';

  if (href) {
    // 외부 링크는 일반 a 태그 사용
    if (target === '_blank') {
      return (
        <a id={id} href={href} className={baseClass} onClick={onClick} target={target} rel={rel}>
          {children}
        </a>
      );
    }

    return (
      <Link id={id} href={href} className={baseClass} onClick={onClick}>
        {children}
      </Link>
    );
  }

  return (
    <button id={id} type="button" className={baseClass} onClick={onClick}>
      {children}
    </button>
  );
}

MenuItem.propTypes = {
  href: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  id: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
};

function UserMenu({ user, pathData, onLogout, t }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const signitureColor = '#ddfc10';

  const displayInitial = pathData?.channelDetail
    ? user.channelUser?.memberNickname?.charAt(0).toUpperCase()
    : user.user?.name?.charAt(0).toUpperCase();

  const backgroundColor = signitureColor;

  const closeMenu = useCallback(() => setIsOpen(false), []);

  const handleLogout = useCallback(() => {
    onLogout();
    closeMenu();
  }, [onLogout, closeMenu]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    const handleEscape = event => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeMenu]);

  return (
    <div ref={menuRef} className="relative">
      <Button
        style={{ backgroundColor }}
        id="userBtn-1"
        variant="ghost"
        size="icon"
        className="w-[35px] h-[35px] rounded-full font-bold text-black hover:opacity-80 active:opacity-70 transition-opacity duration-150 p-0"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="menu">
        {displayInitial}
      </Button>

      {isOpen && (
        <div
          className="absolute top-[calc(100%+10px)] left-1/2 -translate-x-1/2 w-[140px] bg-[#292f38] py-3 px-2 rounded-lg shadow-xl z-50"
          role="menu">
          {/* Arrow */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[8px] border-b-[#292f38]" />

          <MenuItem id="popup-menu-myChannel" href={URL_CONSTANT.myChannel.url} onClick={closeMenu}>
            {t('My Channel')}
          </MenuItem>

          <MenuItem id="popup-menu-myaccount" href={URL_CONSTANT.myAccount.url} onClick={closeMenu}>
            {t('link.myAccount')}
          </MenuItem>

          <MenuItem href={BASIC_CONSTANT.REMOTE_WEB_URL} onClick={closeMenu} target="_blank" rel="noopener noreferrer">
            Mobile Remote
          </MenuItem>

          <MenuItem id="popup-menu-logout" onClick={handleLogout}>
            {t('link.logout')}
          </MenuItem>
        </div>
      )}
    </div>
  );
}

UserMenu.propTypes = {
  user: PropTypes.shape({
    user: PropTypes.shape({
      name: PropTypes.string,
    }),
    channelUser: PropTypes.shape({
      memberNickname: PropTypes.string,
      colorCode: PropTypes.string,
    }),
  }).isRequired,
  pathData: PropTypes.shape({
    channelDetail: PropTypes.bool,
  }).isRequired,
  onLogout: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default UserMenu;
